import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { LoaderService } from '../services/loader/loader.service';
import { IntegrationService } from '../services/integration/integration.service';
import { Integrations, ApiEndpointRequest } from '../models/integration.model';

interface FormHeader {
  name: string;
  value: string;
}

interface EndpointForm {
  displayName: string;
  url: string;
  authType: string;        // UI label: 'None' | 'API Key' | 'Bearer Token'
  authHeaderName: string;
  authSecret: string;
  headers: FormHeader[];
}

// Maps UI label → API enum number
const AUTH_TYPE_MAP: Record<string, number> = {
  'None': 0,
  'API Key': 1,
  'Bearer Token': 2,
};

// Maps API string → UI label (response returns 'None'|'ApiKey'|'BearerToken')
const AUTH_TYPE_DISPLAY: Record<string, string> = {
  'None': 'None',
  'ApiKey': 'API Key',
  'BearerToken': 'Bearer Token',
};

@Component({
  standalone: false,
  selector: 'app-integration.component',
  templateUrl: './integration.component.html',
  styleUrls: ['./integration.component.css']
})
export class IntegrationComponent implements OnInit {

  integrations: Integrations[] = [];
  integrationType = '';

  get activeEndpointCount(): number {
    return this.integrations.filter(i => i.status === 'Active').length;
  }

  emptyStateIntegrations = {
    imgPath: '../../assets/gear-icon.svg',
    message1: 'No API endpoint configurations found for this client.',
    message2: 'Click "New Endpoint" to add one.',
  };

  // Pagination (client-side)
  currentPage = 1;
  readonly pageSize = 7;
  selectedPage = { backgroundColor: '#0F2642', color: 'white', borderRadius: '4px' };

  get totalPages(): number {
    return Math.max(1, Math.ceil(this.integrations.length / this.pageSize));
  }

  get pagedIntegrations(): Integrations[] {
    const start = (this.currentPage - 1) * this.pageSize;
    return this.integrations.slice(start, start + this.pageSize);
  }

  get visiblePages(): number[] {
    const pages: number[] = [];
    const total = this.totalPages;
    const current = this.currentPage;
    const delta = 2;
    const left = Math.max(1, current - delta);
    const right = Math.min(total, current + delta);
    for (let i = left; i <= right; i++) pages.push(i);
    return pages;
  }

  // Row menu
  showMenu = false;
  activeMenuId: number | null = null;
  menuPosition: { top: number; right: number } | null = null;
  activeMenuEndpoint: Integrations | null = null;

  // Modal
  showModal = false;
  isEditMode = false;
  editId: number | null = null;

  // Form
  form: EndpointForm = this.emptyForm();
  showSecret = false;
  urlError = false;
  testResult: { success: boolean; message: string } | null = null;
  isTesting = false;

  readonly authTypes = ['None', 'API Key', 'Bearer Token'];

  constructor(
    private loaderService: LoaderService,
    private integrationService: IntegrationService,
    private router: Router
  ) {}

  ngOnInit(): void {
    if (this.router.url.includes('api-connections')) {
      this.integrationType = 'api';
      this.handleRetrieveIntegrations();
    } else {
      this.handleRetrieveIntegrations();
    }
  }

  private emptyForm(): EndpointForm {
    return { displayName: '', url: '', authType: 'None', authHeaderName: '', authSecret: '', headers: [] };
  }

  navOption(option: string) {
    this.integrationType = option;
    if (option === 'api') {
      this.router.navigate(['/settings/integrations/api-connections']);
    } else if (option === 'cloud') {
      this.router.navigate(['/settings/integrations/cloud-connections']);
    }
  }

  handleRetrieveIntegrations() {
    this.loaderService.showLoader();
    this.integrationService.retrieveApiIntegrations().subscribe({
      next: (value) => {
        this.loaderService.hideLoader();
        this.integrations = value;
      },
      error: (err) => {
        console.error(err);
        this.loaderService.hideLoader();
      }
    });
  }

  // Maps API authType string to UI display label
  getAuthTypeDisplay(apiValue: string): string {
    return AUTH_TYPE_DISPLAY[apiValue] ?? apiValue;
  }

  openNewEndpoint() {
    this.isEditMode = false;
    this.editId = null;
    this.form = this.emptyForm();
    this.showSecret = false;
    this.urlError = false;
    this.testResult = null;
    this.showModal = true;
  }

  openEditEndpoint(endpoint: Integrations) {
    this.closeMenus();
    this.isEditMode = true;
    this.editId = endpoint.apiEndpointConfigId ?? null;
    this.form = {
      displayName: endpoint.displayName,
      url: endpoint.url,
      authType: AUTH_TYPE_DISPLAY[endpoint.authType] ?? endpoint.authType,
      authHeaderName: endpoint.authHeaderName ?? '',
      authSecret: '',   // API never returns the secret
      headers: (endpoint.headers ?? []).map(h => ({ name: h.headerKey, value: h.headerValue }))
    };
    this.showSecret = false;
    this.urlError = false;
    this.testResult = null;
    this.showModal = true;
  }

  closeModal() {
    this.showModal = false;
    this.form = this.emptyForm();
    this.testResult = null;
    this.urlError = false;
    this.showSecret = false;
  }

  validateUrl() {
    const url = this.form.url.trim();
    this.urlError = url.length > 0 && !/^https?:\/\/.+/.test(url);
  }

  isFormValid(): boolean {
    const { displayName, url, authType, authHeaderName, authSecret } = this.form;
    if (!displayName.trim() || !url.trim()) return false;
    if (!/^https?:\/\/.+/.test(url.trim())) return false;
    if (!this.isEditMode) {
      if (authType === 'API Key' && (!authHeaderName.trim() || !authSecret.trim())) return false;
      if (authType === 'Bearer Token' && !authSecret.trim()) return false;
    } else {
      if (authType === 'API Key' && !authHeaderName.trim()) return false;
    }
    return true;
  }

  addHeader() {
    if (this.form.headers.length < 5) this.form.headers.push({ name: '', value: '' });
  }

  removeHeader(index: number) {
    this.form.headers.splice(index, 1);
  }

  toggleMenu(endpoint: Integrations, event: Event) {
    event.stopPropagation();
    const id = endpoint.apiEndpointConfigId ?? 0;
    if (this.activeMenuId === id && this.showMenu) {
      this.showMenu = false;
      this.activeMenuId = null;
      this.menuPosition = null;
      this.activeMenuEndpoint = null;
    } else {
      const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
      this.activeMenuId = id;
      this.showMenu = true;
      this.menuPosition = { top: rect.bottom + 4, right: window.innerWidth - rect.right };
      this.activeMenuEndpoint = endpoint;
    }
  }

  closeMenus() {
    this.showMenu = false;
    this.activeMenuId = null;
    this.menuPosition = null;
    this.activeMenuEndpoint = null;
  }

  testConnection() {
    const url = this.form.url?.trim();
    if (!url || !/^https?:\/\/.+/i.test(url)) {
      this.testResult = { success: false, message: 'URL must start with http:// or https://' };
      return;
    }

    this.isTesting = true;
    this.testResult = null;

    const call$ = this.integrationService.testApiEndpointFromPayload({
      url: this.form.url,
      authType: AUTH_TYPE_MAP[this.form.authType] ?? 0,
      authHeaderName: this.form.authHeaderName || undefined,
      authSecret: this.form.authSecret || undefined,
      headers: this.form.headers
        .filter(h => h.name.trim())
        .map(h => ({ headerKey: h.name, headerValue: h.value }))
    });

    call$.subscribe({
      next: (result: any) => {
        this.isTesting = false;
        this.testResult = result.isSuccess
          ? { success: true,  message: `HTTP ${result.httpStatusCode} OK\n(Connection successful)` }
          : { success: false, message: `HTTP ${result.httpStatusCode}\n(${result.errorMessage ?? result.responseBody ?? 'Connection failed'})` };
      },
      error: (err) => {
        this.isTesting = false;
        this.testResult = {
          success: false,
          message: `HTTP ${err.status ?? 'Error'}\n(${err.error?.message ?? 'Connection failed'})`
        };
      }
    });
  }

  private buildRequestBody(): ApiEndpointRequest {
    return {
      displayName: this.form.displayName,
      url: this.form.url,
      authType: AUTH_TYPE_MAP[this.form.authType] ?? 0,
      authHeaderName: this.form.authHeaderName || undefined,
      authSecret: this.form.authSecret || undefined,
      status: 0, // 0 = Active
      headers: this.form.headers
        .filter(h => h.name.trim())
        .map(h => ({ headerKey: h.name, headerValue: h.value }))
    };
  }

  saveEndpoint() {
    if (!this.isFormValid()) return;
    const body = this.buildRequestBody();
    this.loaderService.showLoader();
    if (this.isEditMode && this.editId != null) {
      this.integrationService.updateApiIntegration(this.editId, body).subscribe({
        next: () => {
          this.loaderService.hideLoader();
          this.closeModal();
          this.handleRetrieveIntegrations();
        },
        error: (err) => {
          console.error(err);
          this.loaderService.hideLoader();
        }
      });
    } else {
      this.integrationService.createApiIntegration(body).subscribe({
        next: () => {
          this.loaderService.hideLoader();
          this.closeModal();
          this.handleRetrieveIntegrations();
        },
        error: (err) => {
          console.error(err);
          this.loaderService.hideLoader();
        }
      });
    }
  }

  toggleStatus(endpoint: Integrations) {
    this.closeMenus();
    const newStatus = this.isActive(endpoint.status) ? 1 : 0; // 1=Inactive, 0=Active
    const body: ApiEndpointRequest = {
      displayName: endpoint.displayName,
      url: endpoint.url,
      authType: AUTH_TYPE_MAP[AUTH_TYPE_DISPLAY[endpoint.authType] ?? endpoint.authType] ?? 0,
      authHeaderName: endpoint.authHeaderName || undefined,
      authSecret: undefined, // null = keep existing secret
      status: newStatus,
      headers: (endpoint.headers ?? []).map(h => ({ headerKey: h.headerKey, headerValue: h.headerValue }))
    };
    this.loaderService.showLoader();
    this.integrationService.toggleStatusApiIntegration(endpoint.apiEndpointConfigId!, body).subscribe({
      next: () => {
        this.loaderService.hideLoader();
        this.handleRetrieveIntegrations();
      },
      error: (err) => {
        console.error(err);
        this.loaderService.hideLoader();
      }
    });
  }

  isActive(status: any): boolean {
    return status === 'Active' || status === 0 || status === 'active';
  }

  // Pagination
  setCurrentPage(page: number) { this.currentPage = page; }

  performIncrement() {
    if (this.currentPage < this.totalPages) this.currentPage++;
  }

  performDecrement() {
    if (this.currentPage > 1) this.currentPage--;
  }
}
